import { CONFIG } from './index.js'
import type { EuRegistrationParameterName } from '@/types/avalaraEuRegistration'

export const API_ENDPOINTS = {
  TAX_CONFIG: {
    CREDENTIAL: 'credential',
    ALL_TAX_CONFIGS:
      CONFIG.API_GATEWAY_URL + 'v1/tax-engine/toolbox/merchant/config',
    MERCHANT_TAX_CONFIG:
      CONFIG.API_GATEWAY_URL + 'v1/tax-engine/toolbox/merchant',
  },
  MERCHANT: {
    ALL_MERCHANTS: CONFIG.API_GATEWAY_URL + 'v1/tax-engine/toolbox/merchant',
  },
  AVALARA: {
    ALL_COMPANIES:
      CONFIG.API_GATEWAY_URL +
      'v1/tax-engine/toolbox/avalara/management/companies',
    MANAGEMENT_COMPANIES:
      CONFIG.API_GATEWAY_URL +
      'v1/tax-engine/toolbox/avalara/management/company',
    /**
     * POST starts the company sync in the background (202, no body).
     * GET returns the run status of the pod that answers.
     */
    MANAGEMENT_COMPANY_SYNC:
      CONFIG.API_GATEWAY_URL +
      'v1/tax-engine/toolbox/avalara/management/company/sync',
    BULK_ALIGN:
      CONFIG.API_GATEWAY_URL +
      'v1/tax-engine/toolbox/avalara/management/company/nexus/align/bulk',
    OVERVIEW_FOR_COMPANY: (companyId: number) =>
      CONFIG.API_GATEWAY_URL +
      `v1/tax-engine/toolbox/avalara/management/company/${companyId}/overview`,
    /** PUT — sets or clears a company's default Avalara tax code */
    SET_DEFAULT_TAX_CODE: (companyId: number) =>
      CONFIG.API_GATEWAY_URL +
      `v1/tax-engine/toolbox/avalara/management/company/${companyId}/default-tax-code`,
    /** GET — the 4 EU registration parameters of a company, read live from Avalara */
    EU_REGISTRATION_FOR_COMPANY: (companyId: number) =>
      CONFIG.API_GATEWAY_URL +
      `v1/tax-engine/toolbox/avalara/management/company/${companyId}/eu-registration`,
    /** PUT adds or resets one parameter, DELETE removes it */
    EU_REGISTRATION_PARAMETER: (companyId: number, parameterKey: EuRegistrationParameterName) =>
      CONFIG.API_GATEWAY_URL +
      `v1/tax-engine/toolbox/avalara/management/company/${companyId}/eu-registration/${parameterKey}`,
    ITEMS_FOR_COMPANY: (companyId: number) =>
      CONFIG.API_GATEWAY_URL +
      `v1/tax-engine/toolbox/avalara/management/company/${companyId}/items`,
    SYNC_ITEMS_FOR_COMPANY: (companyId: number) =>
      CONFIG.API_GATEWAY_URL +
      `v1/tax-engine/toolbox/avalara/management/company/${companyId}/items/sync`,
    ASSIGN_TAX_CODE: (companyId: number, itemId: number) =>
      CONFIG.API_GATEWAY_URL +
      `v1/tax-engine/toolbox/avalara/management/company/${companyId}/items/${itemId}`,
    /** POST — bulk-assigns a single tax code to multiple items */
    BULK_ASSIGN_TAX_CODE: (companyId: number) =>
      CONFIG.API_GATEWAY_URL +
      `v1/tax-engine/toolbox/avalara/management/company/${companyId}/items/bulk-assign`,
    /** POST — adopts Avalara's tax code as the local value; clears drift */
    ACCEPT_DRIFT: (companyId: number, itemId: number) =>
      CONFIG.API_GATEWAY_URL +
      `v1/tax-engine/toolbox/avalara/management/company/${companyId}/items/${itemId}/drift/accept`,
    /** POST — re-pushes local Reach tax code to Avalara; clears drift on success */
    REPUSH_DRIFT: (companyId: number, itemId: number) =>
      CONFIG.API_GATEWAY_URL +
      `v1/tax-engine/toolbox/avalara/management/company/${companyId}/items/${itemId}/drift/repush`,
    TAX_CODE_SEARCH:
      CONFIG.API_GATEWAY_URL + 'v1/tax-engine/toolbox/avalara/tax-code',
    EU_REGISTRATION_CONFIG:
      CONFIG.API_GATEWAY_URL +
      'v1/tax-engine/toolbox/avalara/management/eu-registration-config',
    /** GET — Avalara regions of a country (ListRegions), cached per country by the backend */
    REGIONS_FOR_COUNTRY: (country: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/tax-engine/toolbox/avalara/management/countries/${encodeURIComponent(country)}/regions`,
  },
  FILING_COUNTRY: {
    ALL_FILING_COUNTRIES:
      CONFIG.API_GATEWAY_URL + 'v1/tax-engine/toolbox/filing-country',
    UPDATE_FILING_COUNTRY:
      CONFIG.API_GATEWAY_URL + 'v1/tax-engine/toolbox/filing-country',
    PATCH_FILING_COUNTRY:
      CONFIG.API_GATEWAY_URL + 'v1/tax-engine/toolbox/filing-country',
    GET_CURRENCIES:
      CONFIG.API_GATEWAY_URL + 'v1/tax-engine/toolbox/currencies',
    GET_OANDA_DATA_SETS:
      CONFIG.API_GATEWAY_URL + 'v1/tax-engine/toolbox/oanda-data-sets',
  },
  MERCHANT_CONFIG: {
    GET_MERCHANT_IDS: CONFIG.MERCHANT_API_URL + 'v1/merchants',
    GET_FILING_COUNTRIES: CONFIG.MERCHANT_API_URL + 'v1/filingCountries',
    POST_MERCHANT: CONFIG.MERCHANT_API_URL + 'v1/merchant',
    PUT_MERCHANT: CONFIG.MERCHANT_API_URL + 'v1/merchant',
    GET_COMPANIES_FROM_AVALARA: CONFIG.MERCHANT_API_URL + 'v1/avalaraCompanies',
  },
  COMMON_MERCHANT_SERVICE: {
    ALL_MERCHANTS: CONFIG.API_GATEWAY_URL + 'v1/common-merchant/toolbox',
    SHOPIFY_DEV_APP: (merchantId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/common-merchant/toolbox/${merchantId}/shopify/dev-app`,
    SHOPIFY_DEV_APP_BY_STORE: (merchantId: string, storeId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/common-merchant/toolbox/${merchantId}/shopify/dev-app/${storeId}`,
    RESTORE_DEV_APP: (merchantId: string, storeId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/common-merchant/toolbox/${merchantId}/shopify/dev-app/${storeId}/restore`,
    SHOPIFY_STORE_OFFBOARD: (merchantId: string, storeId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/common-merchant/toolbox/${merchantId}/shopify/store/${storeId}/offboard`,
    SHOPIFY_STORE_GATEWAY_FILTER: (merchantId: string, storeId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/common-merchant/toolbox/${merchantId}/shopify/store/${storeId}/gateway-filter`,
    /** GET / PUT / DELETE — the merchant's Drop-In extensions webhook */
    EXTENSIONS_WEBHOOK: (merchantId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/common-merchant/toolbox/${encodeURIComponent(merchantId)}/extensions-webhook`,
  },
  SESSION_API: {
    /** GET / PUT — the merchant's Drop-In allow-contract default (no DELETE) */
    ALLOW_CONTRACT: (merchantId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/session-api/toolbox/merchants/${encodeURIComponent(merchantId)}/allow-contract`,
  },
  AUTH_SERVER: {
    INTEGRATORS: CONFIG.API_GATEWAY_URL + 'v1/auth-server/toolbox/integrator',
    INTEGRATOR: (integratorId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/auth-server/toolbox/integrator/${encodeURIComponent(integratorId)}`,
    /** PUT links (or moves) the merchant to this integrator; DELETE unlinks it */
    INTEGRATOR_MERCHANT: (integratorId: string, merchantId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/auth-server/toolbox/integrator/${encodeURIComponent(integratorId)}/merchant/${encodeURIComponent(merchantId)}`,
    INTEGRATOR_CREDENTIALS: (integratorId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/auth-server/toolbox/integrator/${encodeURIComponent(integratorId)}/credential`,
    INTEGRATOR_CREDENTIAL: (integratorId: string, username: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/auth-server/toolbox/integrator/${encodeURIComponent(integratorId)}/credential/${encodeURIComponent(username)}`,
  },
  TAX_INJECTOR: {
    UPLOAD_FILE:
      CONFIG.API_GATEWAY_URL + 'v1/tax-engine/toolbox/injector/upload',
    GET_ALL: CONFIG.API_GATEWAY_URL + 'v1/tax-engine/toolbox/injector',
    GET_RECORDS: CONFIG.API_GATEWAY_URL + 'v1/tax-engine/toolbox/injector',
    GET_DOWNLOAD_URL: CONFIG.API_GATEWAY_URL + 'v1/tax-engine/toolbox/injector',
  },
  TRANSACTION_DETAILS: {
    SEARCH_BY_MERCHANT: (merchantId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/transaction-details/toolbox/merchant/${merchantId}/transaction-details`,
    DETAIL: (merchantId: string, externalId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/transaction-details/toolbox/merchant/${merchantId}/transaction-details/${externalId}`,
    CANCEL: (merchantId: string, externalId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/transaction-details/toolbox/merchant/${merchantId}/transaction-details/${externalId}/cancel`,
  },
  TAX_TRANSACTION: {
    SEARCH_BY_MERCHANT: (merchantId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/tax-engine/toolbox/merchant/${merchantId}/tax/transaction`,
    DETAILS: (merchantId: string, transactionId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/tax-engine/toolbox/merchant/${merchantId}/tax/transaction/${transactionId}`,
  },
  FCC: {
    // Base paths for FCC API
    BASE_URL: CONFIG.API_GATEWAY_URL,
    ALERTS: CONFIG.API_GATEWAY_URL + 'v1/toolbox/fcc/alerts',
    MERCHANTS: CONFIG.API_GATEWAY_URL + 'v1/toolbox/fcc/merchants',
    OWNERS: CONFIG.API_GATEWAY_URL + 'v1/toolbox/fcc/owners',
    EVENT_TYPES: CONFIG.API_GATEWAY_URL + 'v1/toolbox/fcc/event-types',
    AVAILABLE_ACTIONS: CONFIG.API_GATEWAY_URL + 'v1/toolbox/fcc/actions',
    BULK_ACTIONS: CONFIG.API_GATEWAY_URL + 'v1/toolbox/fcc/alerts/actions',
    CASES: CONFIG.API_GATEWAY_URL + 'v1/toolbox/fcc/cases',
    JIRA_ISSUES: CONFIG.API_GATEWAY_URL + 'v1/toolbox/fcc/jira/issues',
    // Workato-served FCC analytics, fronted by cloud-gateway.
    // Dates are path segments (YYYY-MM-DD), not query params.
    ANALYTICS: (
      kind: string,
      merchantId: string,
      dateFrom: string,
      dateTo: string,
    ) =>
      CONFIG.API_GATEWAY_URL +
      `v1/toolbox/fcc/merchants/${merchantId}/analytics/${kind}/${dateFrom}/${dateTo}`,
    /**
     * Manual supplier notes: GET lists every live note, POST creates one. A 404
     * means the supplier is not in the FCC roster, not that the read failed.
     */
    SUPPLIER_NOTES: (merchantId: string) =>
      CONFIG.API_GATEWAY_URL + `v1/toolbox/fcc/merchants/${merchantId}/notes`,
    /**
     * PATCH edits or soft-deletes one note; the verb is carried in the body as
     * `{ action: 'EDIT' | 'DELETE' }`, so there is no DELETE endpoint.
     */
    SUPPLIER_NOTE: (merchantId: string, noteId: number) =>
      CONFIG.API_GATEWAY_URL +
      `v1/toolbox/fcc/merchants/${merchantId}/notes/${noteId}`,
  },
  NETSUITE: {
    /**
     * GET returns the vendor; PATCH `{ PaymentHold: boolean }` is the whole
     * hold/release toggle. Path tail must match the endpoint name in Workato's
     * Toolbox API collection.
     */
    VENDOR: (vendorName: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/toolbox/netsuite/vendor/${encodeURIComponent(vendorName)}`,
  },
  DE_MINIMIS_GROUPS: {
    BASE:
      CONFIG.API_GATEWAY_URL +
      'v1/tax-engine/toolbox/deminimis-exemption-group',
    GET_BY_ID:
      CONFIG.API_GATEWAY_URL +
      'v1/tax-engine/toolbox/deminimis-exemption-group',
    CREATE:
      CONFIG.API_GATEWAY_URL +
      'v1/tax-engine/toolbox/deminimis-exemption-group',
    UPDATE:
      CONFIG.API_GATEWAY_URL +
      'v1/tax-engine/toolbox/deminimis-exemption-group',
    DELETE:
      CONFIG.API_GATEWAY_URL +
      'v1/tax-engine/toolbox/deminimis-exemption-group',
    SEARCH:
      CONFIG.API_GATEWAY_URL +
      'v1/tax-engine/toolbox/deminimis-exemption-group/search',
  },
  SHOPIFY_PAYOUTS_EMBEDDED: {
    PAYOUTS:
      CONFIG.API_GATEWAY_URL +
      'v1/shopify-payments-embedded/toolbox/merchant',
    PENDING_ASSIGNMENT:
      CONFIG.API_GATEWAY_URL +
      'v1/shopify-payments-embedded/toolbox/merchant/store/business-entity/payout/pending-bank-account-assignment',
    SLACK_NOTIFICATIONS: {
      SUBSCRIBE:
        CONFIG.API_GATEWAY_URL +
        'v1/shopify-payments-embedded/toolbox/payout-assignment-notification/slack/subscribe',
      UNSUBSCRIBE:
        CONFIG.API_GATEWAY_URL +
        'v1/shopify-payments-embedded/toolbox/payout-assignment-notification/slack/unsubscribe',
      SUBSCRIPTION:
        CONFIG.API_GATEWAY_URL +
        'v1/shopify-payments-embedded/toolbox/payout-assignment-notification/slack/subscription',
      TEST:
        CONFIG.API_GATEWAY_URL +
        'v1/shopify-payments-embedded/toolbox/payout-assignment-notification/slack/test',
    },
  },
  SHOPIFY_FETCH: {
    PAYOUTS: (merchantId: string, storeId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/shopify-fetch/toolbox/merchants/${merchantId}/stores/${storeId}/payouts`,
    ORDERS: (merchantId: string, storeId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/shopify-fetch/toolbox/merchants/${merchantId}/stores/${storeId}/orders`,
  },
  ACCT_LIFECYCLE: {
    ONBOARDING:
      CONFIG.API_GATEWAY_URL + 'v1/acct-lifecycle-mgmt/toolbox/onboarding',
    RESEND_ACTIVATION:
      CONFIG.API_GATEWAY_URL +
      'v1/acct-lifecycle-mgmt/toolbox/onboarding/resend-activation',
    ACCOUNTS: CONFIG.API_GATEWAY_URL + 'v1/acct-lifecycle-mgmt/toolbox/account',
    PARTNER_BASE:
      CONFIG.API_GATEWAY_URL + 'v1/acct-lifecycle-mgmt/toolbox/partner',
    PARTNER_BY_ID: (externalId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/acct-lifecycle-mgmt/toolbox/partner/${encodeURIComponent(externalId)}`,
    PARTNER_TOKEN: (externalId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/acct-lifecycle-mgmt/toolbox/partner/${encodeURIComponent(externalId)}/token`,
    PARTNER_TOKEN_BY_ID: (externalId: string, tokenId: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/acct-lifecycle-mgmt/toolbox/partner/${encodeURIComponent(externalId)}/token/${encodeURIComponent(tokenId)}`,
    ACCOUNT_DOCUMENT_VIEWER_LINK: (externalId: string, documentUuid: string) =>
      CONFIG.API_GATEWAY_URL +
      `v1/acct-lifecycle-mgmt/toolbox/account/${encodeURIComponent(externalId)}/documents/${encodeURIComponent(documentUuid)}/viewer-link`,
  },
}
